import React from 'react';
import {Box, Checkbox, FormControlLabel, FormGroup, Link, Tab, Tabs, Typography, useTheme} from '@mui/material';
import {DocumentCategories, LogInType, ResourceTabs} from 'app/types/Shared';
import {StyledTooltip} from 'components/StyledTooltip/StyledTooltip';
import {useSelector} from 'react-redux';
import {selectUserType} from 'app/store/users/userSlice';

interface DocumentFiltersProps {
  filters: Array<DocumentCategories>;
  activeFilters: Array<DocumentCategories>;
  currentTabIndex: number;
  tabs?: Array<ResourceTabs>;
  onFilter: (filter: Array<DocumentCategories>) => void;
  onTabChange: (tab: ResourceTabs, tabIndex: number) => void;
  veiwAllCallback?: () => Promise<void>;
}

const Checkmark = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M5 9.31034L8.31034 13.0345L15.3448 6'
      stroke='#450A0A'
      strokeWidth='3'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const DocumentFilters: React.FC<DocumentFiltersProps> = ({
  filters,
  activeFilters,
  onFilter,
  veiwAllCallback,
  onTabChange,
  currentTabIndex,
  tabs = Object.values(ResourceTabs),
}) => {
  const userType = useSelector(selectUserType);
  const theme = useTheme();

  const handleViewAllClick = () => {
    if (veiwAllCallback) {
      veiwAllCallback();
    }
  };

  function a11yProps(name: string) {
    return {
      id: name,
    };
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (event) {
      const tab = Object.values(ResourceTabs)[newValue];
      onTabChange(tab, newValue);
    }
  };

  const handleFilterChange = (label: DocumentCategories) => {
    if (activeFilters.includes(label)) {
      onFilter(activeFilters.filter((item) => item !== label));
    } else {
      onFilter([...activeFilters, label]);
    }
  };

  return (
    <Box sx={{zIndex: 100}}>
      <Tabs
        value={currentTabIndex}
        onChange={handleChange}
        textColor='secondary'
        sx={{
          px: 6,
          minWidth: 0,
          minHeight: 0,
        }}
        TabIndicatorProps={{
          style: {
            display: 'none',
          },
        }}
      >
        {Object.values(ResourceTabs).map((item, index) => (
          <Tab
            key={item + index}
            disabled={!tabs.includes(item)}
            label={
              <Box
                px='20px'
                height='39px'
                display='flex'
                alignItems='center'
                sx={
                  //Disable tabs if they aren't in the list of available tabs
                  tabs.includes(item)
                    ? {
                        bgcolor: currentTabIndex === index ? 'primary.main' : theme.palette.grey[100],
                        border:
                          currentTabIndex === index
                            ? `1px solid ${theme.palette.primary.main}`
                            : `1px solid ${theme.palette.grey[500]}`,

                        borderBottom: 'none',
                        color: currentTabIndex === index ? 'white' : 'grey.800',
                        '&:hover': {
                          backgroundColor: 'white',
                          border: `1px solid ${theme.palette.primary.main}`,
                          color: theme.palette.primary.main,
                        },
                      }
                    : {
                        bgcolor: theme.palette.grey[50],
                        border: `1px solid ${theme.palette.grey[500]}`,
                        color: theme.palette.grey[600],
                      }
                }
              >
                <Typography variant='h5'>{item}</Typography>
              </Box>
            }
            disableRipple
            {...a11yProps(item)}
            sx={{
              p: 0,
              mr: '3px',
              minWidth: 0,
              minHeight: 0,
            }}
          />
        ))}
      </Tabs>
      <Box
        width='100%'
        borderTop='1px solid #D9D9D9'
        sx={{zIndex: 100, position: 'relative'}}
        display='flex'
        justifyContent='flex-start'
        alignItems='center'
        minHeight='55px'
        px={6}
        boxShadow='0px 2px 5px 0px rgb(0,0,0,0.15)'
      >
        <FormGroup row>
          {Object.values(DocumentCategories)
            .filter((x) => {
              if (userType === LogInType.External) {
                return x === DocumentCategories.BusinessControlled || x === DocumentCategories.Processes;
              } else {
                return true;
              }
            })
            .map((filter) => (
              <StyledTooltip
                key={filter}
                title={
                  <Typography variant='h6'>
                    Non-QMS documents maintained by the business that outline best practices, guidance, tip sheets, and
                    other supportive information for business processes and systems.
                  </Typography>
                }
                sx={{display: filter == DocumentCategories.BusinessControlled ? 'block' : 'none'}}
              >
                <FormControlLabel
                  key={filter}
                  control={
                    <Checkbox
                      sx={{
                        '&.Mui-disabled': {
                          color: 'red',
                        },
                      }}
                      checkedIcon={
                        <Box
                          sx={{
                            bgcolor: 'white',
                            maxHeight: '20px',
                            maxWidth: '20px',
                            minHeight: '20px',
                            minWidth: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${theme.palette.grey[500]}`,
                          }}
                        >
                          <Checkmark />
                        </Box>
                      }
                      icon={
                        <Box
                          sx={{
                            border: `1px solid ${theme.palette.grey[500]}`,
                            maxHeight: '20px',
                            maxWidth: '20px',
                            minHeight: '20px',
                            minWidth: '20px',
                            bgcolor: filters?.includes(filter) ? 'white' : theme.palette.grey[500],
                          }}
                        />
                      }
                      checked={activeFilters.includes(filter)}
                      disabled={!filters?.includes(filter)}
                      onChange={() => handleFilterChange(filter)}
                    />
                  }
                  label={
                    <Typography variant='h6' fontWeight={600}>
                      {filter}
                    </Typography>
                  }
                  disabled={!filters?.includes(filter)}
                />
              </StyledTooltip>
            ))}
        </FormGroup>
        <Box flexGrow={1} display='flex' flexDirection='column' justifyContent='center' alignItems='flex-end'>
          {veiwAllCallback && (
            <Link
              underline='hover'
              sx={{cursor: 'pointer'}}
              color='primary'
              display='flex'
              alignItems='flex-end'
              onClick={() => handleViewAllClick()}
            >
              View All Available Resources
            </Link>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DocumentFilters;
